import { cn, cva, NoddiIcon, type VariantProps } from 'noddi-ui-common';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { noddiToast } from '../Feedback/NoddiToaster/toast';

const copyButtonVariants = cva('flex items-center justify-center rounded-sm transition-colors focus:outline-hidden', {
  variants: {
    variant: {
      default: 'bg-gray-100 p-1 hover:bg-gray-200',
      ghost: 'p-1 hover:bg-gray-100'
    },
    size: {
      default: 'size-6',
      small: 'size-5',
      large: 'size-9'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});

interface NoddiCopyButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof copyButtonVariants> {
  textToCopy: string;
  successMessage?: string;
  errorMessage?: string;
  disabled?: boolean;
}

export const NoddiCopyButton = forwardRef<HTMLButtonElement, NoddiCopyButtonProps>(
  (
    {
      textToCopy,
      successMessage,
      errorMessage = 'Failed to copy to clipboard',
      disabled = false,
      variant = 'default',
      size = 'default',
      className,
      ...props
    },
    ref
  ) => {
    const handleCopyToClipboard = async () => {
      if (!textToCopy || textToCopy === '-') {
        noddiToast.error('Nothing to copy');
        return;
      }

      try {
        await navigator.clipboard.writeText(textToCopy);
        const message = successMessage || 'Copied to clipboard!';
        noddiToast.success(message);
      } catch {
        noddiToast.error(errorMessage);
      }
    };

    const isDisabled = disabled || !textToCopy || textToCopy === '-';

    return (
      <button
        ref={ref}
        type='button'
        // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleCopyToClipboard(); React discards the returned promise, but the body catches, so nothing escapes
        onClick={handleCopyToClipboard}
        disabled={isDisabled}
        className={cn(copyButtonVariants({ variant, size }), className)}
        title='Copy to clipboard'
        {...props}
      >
        <NoddiIcon
          name='Copy'
          size={size === 'small' ? 'xSmall' : size === 'large' ? 'small' : 'small'}
          className={cn('text-gray-600', isDisabled && 'opacity-50', variant === 'ghost' && 'text-gray-500')}
        />
      </button>
    );
  }
);

NoddiCopyButton.displayName = 'NoddiCopyButton';

export type NoddiCopyButtonVariant = VariantProps<typeof copyButtonVariants>['variant'];
